import clsx from 'clsx';
import {
  ChevronsUpDown,
  FileDiff,
  Gauge,
  Languages,
  LogOut,
  Menu,
  MessageSquareWarning,
  Moon,
  Phone,
  Sun,
  Ticket,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { companyName, signedInName } from '../lib/people';
import { VigLogo } from './Logo';

/** The portal's places, and the account. */
const NAV: Array<{ to: string; label: TranslationKey; icon: typeof Gauge }> = [
  { to: '/', label: 'nav.dashboard', icon: Gauge },
  { to: '/tickets', label: 'nav.tickets', icon: Ticket },
  { to: '/change-requests', label: 'nav.changeRequests', icon: FileDiff },
  // A complaint about someone on the client's projects (operator, 2026-10-28).
  { to: '/complaints', label: 'nav.complaints', icon: MessageSquareWarning },
  // Calls to the people Viganium chose for the client (operator, 2026-11-01).
  { to: '/calls', label: 'nav.calls', icon: Phone },
];

const THEME_KEY = 'viganium.portal.theme';

/** The theme the client chose here, or else the one their system prefers. */
function initialTheme(): 'dark' | 'light' {
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    /* storage blocked */
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(initialTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      /* the choice just isn't remembered */
    }
  };
  return { theme, toggle };
}

export function Layout({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n();
  const { account } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  // The language the portal is in is remembered on the account too.
  const syncedLocale = useRef(account?.locale);
  useEffect(() => {
    if (!account || syncedLocale.current === locale) return;
    syncedLocale.current = locale;
    void api.patch('/auth/me/locale', { locale }).catch(() => {
      syncedLocale.current = undefined;
    });
  }, [locale, account]);

  const company = companyName(account, locale);
  // A contact's own sign-in shows their name on the card; the company's own shows the company's.
  const person = signedInName(account, locale);

  return (
    <div className="flex h-full">
      {/* Sidebar — the dark console surface, in both themes, as in the admin. */}
      <aside
        data-theme="dark"
        className={clsx(
          'fixed inset-y-0 start-0 z-overlay flex w-sidebar flex-col bg-ink-deep',
          'border-e border-ink-line transition-[transform,visibility] duration-base ease-precision',
          // A wide window's sidebar is part of the page, with no layer of its own: the call's windows — the dock, a
          // shared screen — float over it (2026-11-02, the operator's screenshot of a shared screen half under it).
          // Below lg it is the drawer, and opens over them.
          'lg:static lg:z-auto lg:translate-x-0',
          // Scoped to `max-lg`: `ltr:`/`rtl:` outrank `lg:`, so an unscoped
          // transform would hide the sidebar on a desktop too. Closed, it is
          // invisible too: switching the language from the top bar flips it from
          // one side to the other, and the slide would sweep across the page.
          mobileOpen
            ? 'translate-x-0'
            : 'max-lg:invisible max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
        )}
      >
        <div className="relative flex h-topbar items-center border-b border-ink-line px-4">
          <Link to="/" className="flex min-w-0 flex-1 flex-col items-center gap-1" aria-label={t('app.name')}>
            <VigLogo className="h-6 w-auto text-neutral-50" />
            <span className="block w-full truncate text-center text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">
              {t('app.surface')}
            </span>
          </Link>
          <button
            type="button"
            className="absolute end-3 top-1/2 -translate-y-1/2 rounded-sm p-1 text-neutral-400 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3">
          <ul className="space-y-0.5">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    clsx(
                      'group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-body-sm font-medium',
                      'transition-colors duration-fast ease-precision',
                      isActive
                        ? 'bg-primary/15 text-neutral-50 shadow-[inset_2px_0_0_0_#C7102E] rtl:shadow-[inset_-2px_0_0_0_#C7102E]'
                        : 'text-neutral-400 hover:bg-white/5 hover:text-neutral-100',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">{t(item.label)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* The language moved to the top bar, and Account and Sign out open from
            the name card, as in the admin (operator, 2026-10-10). */}
        <div className="border-t border-ink-line p-3">
          <AccountMenu name={person} username={account?.username} />
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-sticky bg-ink-deep/60 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-top-bar
          className="sticky top-0 z-sticky flex h-topbar shrink-0 items-center gap-3 border-b hairline bg-[var(--surface-card)] px-4 sm:px-6"
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] lg:hidden"
            aria-label={t('nav.openMenu')}
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="min-w-0 truncate text-body-sm font-medium text-[var(--text-secondary)]">{company}</span>
          <div className="ms-auto flex items-center gap-1">
            <LanguageToggle />
            <button
              type="button"
              onClick={toggle}
              className="rounded-sm p-2 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
              aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
              title={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}

/**
 * The other language, in the top bar (operator, 2026-10-10: "move change
 * languages to top"). Named in that language; only its icon on a phone.
 */
function LanguageToggle() {
  const { locale, setLocale } = useI18n();
  const other = locale === 'en' ? 'العربية' : 'English';
  return (
    <button
      type="button"
      onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
      lang={locale === 'en' ? 'ar' : 'en'}
      aria-label={other}
      className="flex h-9 items-center gap-1.5 rounded-sm px-2 text-body-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
    >
      <Languages className="h-4 w-4" aria-hidden />
      <span className="hidden sm:inline">{other}</span>
    </button>
  );
}

/**
 * Whoever is signed in — a contact, or the company's own sign-in — at the foot
 * of the sidebar. Account and Sign out open from it, above the card, as in the
 * admin (operator, 2026-10-10).
 */
function AccountMenu({ name, username }: { name: string; username?: string }) {
  const { t } = useI18n();
  const { signOut } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx(
          'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start',
          'transition-colors duration-fast ease-precision hover:bg-white/10',
          open ? 'bg-white/10' : 'bg-white/5',
        )}
      >
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/25 text-caption font-semibold text-primary-300"
          aria-hidden
        >
          {initials(name || '?')}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-neutral-100">{name}</span>
          <span className="block truncate text-caption text-[var(--text-tertiary)]" dir="ltr">
            {username}
          </span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute inset-x-0 bottom-full z-dropdown mb-1.5 rounded-md border border-ink-line bg-ink-card p-1.5 shadow-lg"
        >
          <Link role="menuitem" to="/account" onClick={() => setOpen(false)} className={ACCOUNT_ITEM}>
            <UserRound className="h-4 w-4 shrink-0" aria-hidden />
            {t('nav.account')}
          </Link>
          <button role="menuitem" type="button" onClick={() => void signOut()} className={ACCOUNT_ITEM}>
            <LogOut className="h-4 w-4 shrink-0 flip-rtl" aria-hidden />
            {t('auth.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

const ACCOUNT_ITEM =
  'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-start text-body-sm text-neutral-300 transition-colors duration-fast hover:bg-white/5 hover:text-neutral-50';

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
